/**
 * Element Plus el-table-v2 滚轮行为对齐普通 el-table（原生滚动链）。
 *
 * 背景：el-table-v2 body 的 wheel 监听(use-wheel)在表格有滚动余量时
 * preventDefault 并自行 scrollTo；仅在到达边缘时放行原生滚动链。空表/
 * 无余量表格会持续 preventDefault 吞掉滚轮，外层页面滚不动。
 *
 * 上一版修复（capture 阶段 stopImmediatePropagation 阻断 EP 的 wheel，
 * 交还浏览器原生链）存在判据缺陷：阻断条件用
 * `scroller.scrollHeight > scroller.clientHeight` 判断"表格有余量"，
 * 但虚拟列表视口(el-vl__wrapper, overflow:visible)的 scrollHeight
 * 恒等于 clientHeight（内容被虚拟化，不用占位元素撑高），判据永远为
 * "无余量"——所有 v2 表格（组织架构左侧部门树、销售数据各维度表格）
 * 的 EP 滚轮被一刀切阻断：表格自身滚轮失灵，滚轮错位到外层整页滚动。
 *
 * 本版改为"观察式补偿"，不再阻断 EP：
 * 1. 放行 Element Plus 的 wheel 处理——有余量的表格照常滚轮滚动，
 *    到达边缘时 EP 自身放行原生滚动链（与普通 el-table 一致）；
 * 2. 一帧后检查表格内部与页面均未滚动（EP 吞掉滚轮的空表/无余量场景），
 *    手动滚动最近的可滚祖先，恢复原生滚动链语义。
 */
const findTableV2Scroller = (target: HTMLElement | null): HTMLElement | null => {
  if (!target || typeof target.closest !== 'function') return null
  return target.closest('.el-table-v2__body, .el-vl__wrapper, .el-vl__window') as HTMLElement | null
}

const readScrollerState = (scroller: HTMLElement) => {
  // closest 可能命中 .el-vl__window 自身：自身 transform 与子级 window 的 transform 都要观察
  const win = scroller.classList.contains('el-vl__window')
    ? scroller
    : scroller.querySelector<HTMLElement>('.el-vl__window')
  return {
    scrollTop: scroller.scrollTop,
    winTransform: win ? win.style.transform : ''
  }
}

const scrollNearestScrollableAncestor = (from: HTMLElement, deltaY: number): boolean => {
  let el: HTMLElement | null = from.parentElement
  while (el && el !== document.body) {
    const cs = window.getComputedStyle(el)
    const scrollable =
      (cs.overflowY === 'auto' || cs.overflowY === 'scroll' || cs.overflow === 'auto' || cs.overflow === 'scroll') &&
      el.scrollHeight > el.clientHeight + 1
    if (scrollable && (deltaY < 0 ? el.scrollTop > 0 : el.scrollTop + el.clientHeight < el.scrollHeight)) {
      el.scrollTop = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, el.scrollTop + deltaY))
      return true
    }
    el = el.parentElement
  }
  // 兜底：主内容滚动容器（el-scrollbar 包裹层）
  const mainWrap = document.querySelector<HTMLElement>('.v-layout-content-scrollbar .el-scrollbar__wrap')
  if (
    mainWrap &&
    mainWrap.scrollHeight > mainWrap.clientHeight + 1 &&
    (deltaY < 0 ? mainWrap.scrollTop > 0 : mainWrap.scrollTop + mainWrap.clientHeight < mainWrap.scrollHeight)
  ) {
    mainWrap.scrollTop = Math.max(
      0,
      Math.min(mainWrap.scrollHeight - mainWrap.clientHeight, mainWrap.scrollTop + deltaY)
    )
    return true
  }
  return false
}

export const installTableV2WheelScrollFix = () => {
  if (typeof window === 'undefined') return
  const w = window as unknown as { __tableV2WheelScrollFix?: boolean }
  if (w.__tableV2WheelScrollFix) return
  w.__tableV2WheelScrollFix = true

  window.addEventListener(
    'wheel',
    (event) => {
      if (Math.abs(event.deltaY) <= 0) return
      const scroller = findTableV2Scroller(event.target as HTMLElement | null)
      if (!scroller) return
      const before = readScrollerState(scroller)
      const pageBefore = window.scrollY
      requestAnimationFrame(() => {
        const after = readScrollerState(scroller)
        const tableScrolled =
          after.scrollTop !== before.scrollTop || after.winTransform !== before.winTransform
        const pageScrolled = window.scrollY !== pageBefore
        if (tableScrolled || pageScrolled) return
        // EP 吞掉了滚轮且原生链未生效：手动滚最近可滚祖先恢复滚动链
        scrollNearestScrollableAncestor(scroller, event.deltaY)
      })
    },
    { capture: false, passive: true }
  )
}
